import { useEffect, useRef, useState } from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import UploadOutlineIcon from 'mdi-react/UploadOutlineIcon';
import {
  UPLOAD_PROGRESS_EVENT,
  UPLOAD_FIELD_LABELS,
  UploadProgressEventDetail,
} from 'services/uploadLifecycle';

const expandReplacements = (ids: string[], uploads: Record<string, UploadProgressEventDetail>): string[] => {
  const replacements = new Set<string>();
  const pending = [...ids];
  while (pending.length > 0) {
    const id = pending.pop()!;
    if (replacements.has(id)) continue;
    replacements.add(id);
    pending.push(...(uploads[id]?.replaces ?? []));
  }
  return [...replacements];
};

const UploadProgress = () => {
  const intl = useIntl();
  const [uploads, setUploads] = useState<Record<string, UploadProgressEventDetail>>({});
  const [dismissed, setDismissed] = useState(false);
  const dismissTimers = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const timerMap = dismissTimers.current;
    const handleProgress = (event: Event) => {
      const { detail } = event as CustomEvent<UploadProgressEventDetail>;
      if (detail.state === 'preparing') setDismissed(false);
      setUploads((current) => {
        // Only replace failures that existed before this attempt began. A
        // concurrent successful photo must not hide another photo's new failure.
        const replaces = detail.state === 'preparing'
          ? expandReplacements(detail.replaces ?? [], current)
          : current[detail.id]?.replaces ?? [];
        const remaining = Object.fromEntries(Object.entries(current).filter(([id]) =>
          detail.state !== 'complete' || !replaces.includes(id)));
        return { ...remaining, [detail.id]: { ...detail, replaces } };
      });

      const previousTimer = timerMap.get(detail.id);
      if (previousTimer) {
        window.clearTimeout(previousTimer);
        timerMap.delete(detail.id);
      }
      if (detail.state === 'complete') {
        const timer = window.setTimeout(() => {
          setUploads((current) => {
            const { [detail.id]: _dismissed, ...remaining } = current;
            return remaining;
          });
          timerMap.delete(detail.id);
        }, 2000);
        timerMap.set(detail.id, timer);
      }
    };

    window.addEventListener(UPLOAD_PROGRESS_EVENT, handleProgress);
    return () => {
      window.removeEventListener(UPLOAD_PROGRESS_EVENT, handleProgress);
      timerMap.forEach((timer) => window.clearTimeout(timer));
      timerMap.clear();
    };
  }, []);

  const activeUploads = Object.values(uploads);
  if (dismissed || activeUploads.length === 0) {
    return null;
  }

  const uploadStatus = (upload: UploadProgressEventDetail) => {
    switch (upload.state) {
      case 'preparing': return intl.formatMessage({ id: 'general.upload-progress.preparing' });
      case 'uploading': return intl.formatMessage(
        { id: 'general.upload-progress.uploading' },
        { progress: upload.progress },
      );
      case 'complete': return intl.formatMessage({ id: 'general.upload-progress.complete' });
      case 'finalizing': return intl.formatMessage({ id: 'general.upload-progress.finalizing' });
      case 'failed': return intl.formatMessage({ id: 'general.upload-progress.failed' });
      case 'cancelled': return intl.formatMessage({ id: 'general.upload-progress.cancelled' });
      default: return '';
    }
  };

  return (
    <div
      className="position-fixed"
      style={{ right: '1rem', bottom: '1rem', zIndex: 1060, width: 'min(24rem, calc(100vw - 2rem))' }}
      role="region"
      aria-label={intl.formatMessage({ id: 'general.upload-progress.title' })}
    >
      <Card className="shadow">
        <Card.Header className="font-weight-bold pt-4 pb-3 d-flex align-items-center justify-content-between">
          <span className="d-flex align-items-center">
            <UploadOutlineIcon size={20} className="mr-2" aria-hidden="true" />
            {intl.formatMessage({ id: 'general.upload-progress.title' })}
          </span>
          <button
            type="button"
            className="close ml-3"
            aria-label={intl.formatMessage({ id: 'general.close' })}
            onClick={() => {
              setDismissed(true);
              setUploads((current) => Object.fromEntries(
                Object.entries(current).filter(([, upload]) =>
                  !['failed', 'cancelled', 'complete'].includes(upload.state)),
              ));
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Card.Header>
        <Card.Body className="pt-2 pb-4">
          {activeUploads.map((upload) => (
            <div key={upload.id} className="mb-2">
              <div className="font-weight-bold mb-1">
                {intl.formatMessage({ id: UPLOAD_FIELD_LABELS[upload.field] ?? 'general.image' })}
              </div>
              <div className="mb-1" aria-hidden="true">
                {uploadStatus(upload)}
              </div>
              <div className="sr-only" role="alert" aria-live="assertive">
                {upload.state === 'failed' ? uploadStatus(upload) : ''}
              </div>
              <div className="sr-only" role="status" aria-live="polite">
                {['complete', 'cancelled'].includes(upload.state) ? uploadStatus(upload) : ''}
              </div>
              <ProgressBar>
                <ProgressBar
                  now={upload.progress}
                  aria-label={uploadStatus(upload)}
                  variant={upload.state === 'failed' ? 'danger' : upload.state === 'complete' ? 'success' : 'info'}
                  animated={['preparing', 'uploading', 'finalizing'].includes(upload.state)}
                  striped={['preparing', 'uploading', 'finalizing'].includes(upload.state)}
                />
              </ProgressBar>
            </div>
          ))}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UploadProgress;
